import { Button } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import medicalReviewApi, {
  MedicalReviewApiError,
} from "@/services/medicalReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import { invalidateReviewDatasets } from "@/views/MedicalReview/medicalReview";
import type { ReviewMarkTarget } from "@/views/MedicalReview/medicalReview.types";

const MARKED_TOAST_KEY = "medical-review-marked";

/**
 * Marking and reopening records, with the notifications both need. Every
 * outcome is surfaced: a record whose data moved since the reviewer saw it is
 * refused by the API and must be looked at again rather than counted reviewed.
 */
export const useReviewMarkMutations = (
  trialId: string,
  onSettled?: () => void,
) => {
  const queryClient = useQueryClient();
  const {
    openSuccessNotification,
    openErrorNotification,
    closeNotification,
    contextHolder,
  } = useAppNotification();

  const invalidate = () => {
    invalidateReviewDatasets(queryClient);
    queryClient.invalidateQueries({
      queryKey: ["medical-review-queue", trialId],
    });
    queryClient.invalidateQueries({
      queryKey: ["medical-review-subject", trialId],
    });
    queryClient.invalidateQueries({
      queryKey: ["medical-review-counts", trialId],
    });
    queryClient.invalidateQueries({ queryKey: ["medical-review-item"] });
  };

  const reopenMutation = useMutation({
    mutationFn: (targets: ReviewMarkTarget[]) =>
      medicalReviewApi.mark(trialId, {
        items: targets,
        action: "review_reopened",
      }),
    onSuccess: (result) => {
      closeNotification(MARKED_TOAST_KEY);
      invalidate();
      onSettled?.();
      if (result.marked.length) {
        openSuccessNotification({
          title: `${result.marked.length} ${result.marked.length === 1 ? "record" : "records"} reopened`,
          description:
            "The record is back in the queue. The original review and this reopening both stay in the history.",
        });
      }
      if (result.stale.length) {
        openErrorNotification({
          title: `${result.stale.length} could not be reopened`,
          description:
            "The data changed since you saw it, so the record is already back in the queue for re-review.",
        });
      }
      if (result.not_reviewed.length) {
        openErrorNotification({
          title: "Nothing to reopen",
          description:
            "That record is already awaiting review — the review was reopened or superseded elsewhere.",
        });
      }
    },
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not reopen the review",
        description: error.message,
      }),
  });

  const markMutation = useMutation({
    mutationFn: (targets: ReviewMarkTarget[]) =>
      medicalReviewApi.mark(trialId, { items: targets }),
    onSuccess: (result, targets) => {
      invalidate();
      onSettled?.();
      if (result.marked.length) {
        const markedTargets = targets.filter((target) =>
          result.marked.includes(target.item_id),
        );
        openSuccessNotification({
          key: MARKED_TOAST_KEY,
          title: `${result.marked.length} ${result.marked.length === 1 ? "record" : "records"} reviewed`,
          description:
            "Your review is recorded as your electronic signature — your name, the time, and the exact content you reviewed.",
          duration: 8,
          actions: (
            <Button
              size="small"
              onClick={() => reopenMutation.mutate(markedTargets)}
            >
              Reopen
            </Button>
          ),
        });
      }
      if (result.stale.length) {
        openErrorNotification({
          title: `${result.stale.length} could not be marked`,
          description:
            "The data changed while you were reviewing. They are back in the queue with the new values.",
        });
      }
    },
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not record your review",
        description: error.message,
      }),
  });

  return {
    markItems: markMutation.mutate,
    reopenItems: reopenMutation.mutate,
    isMarking: markMutation.isPending || reopenMutation.isPending,
    reportUnmarkable: (count: number) =>
      openErrorNotification({
        title: `${count} ${count === 1 ? "record" : "records"} could not be included`,
        description:
          "Their reviewed content could not be identified, so they were left out rather than signed for blind. Open each record to review it.",
      }),
    openErrorNotification,
    contextHolder,
  };
};
